import 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export type SectionIndexRow = {
    id: string;
    kind: 'item' | 'section';
    num: string;
    title: string;
    status?: string;
    pageCode?: string;
};

export type SectionIndexViewProps = {
    header: AuditProgramSectionVO;
    refCode: string;
    sectionLetter: string;
    sectionTitle: string;
    rows: SectionIndexRow[];
    onNavigate?: (pageCode: string) => void;
};

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 1,
};
const bodySx = {
    ...cellBorder,
    py: 0.6,
    px: 1,
    verticalAlign: 'top' as const,
};

/**
 * Section TOC page (Excel Ai / Bi / Ci style): Ref | Description.
 */
export default function SectionIndexView({
    header,
    refCode,
    sectionLetter,
    sectionTitle,
    rows,
    onNavigate,
}: SectionIndexViewProps) {
    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader header={header} variant="a1" refCode={refCode}/>
            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', mt: 1, mb: 2, letterSpacing: 0.5}}
            >
                {sectionLetter}&nbsp;&nbsp;&nbsp;{sectionTitle}
            </Typography>
            <TableContainer>
                <Table size="small" sx={{'& td, & th': cellBorder}}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{...headSx, width: 72}}>Ref</TableCell>
                            <TableCell sx={headSx}>Description</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const clickable = Boolean(row.pageCode && onNavigate);
                            const isSection = row.kind === 'section';
                            return (
                                <TableRow
                                    key={row.id}
                                    hover={clickable}
                                    onClick={clickable ? () => onNavigate?.(row.pageCode!) : undefined}
                                    sx={clickable ? {cursor: 'pointer'} : undefined}
                                >
                                    <TableCell
                                        sx={{
                                            ...bodySx,
                                            fontWeight: isSection || clickable ? 600 : 400,
                                            color: clickable ? 'primary.main' : 'text.primary',
                                        }}
                                    >
                                        {row.num}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            ...bodySx,
                                            fontWeight: isSection ? 600 : 400,
                                            color: clickable ? 'primary.main' : 'text.primary',
                                        }}
                                    >
                                        {row.title}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
